import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { apiClient, handleApiRequest } from '@/services/api/client'
import { AdminSlideOver } from '@/components/radar/AdminSlideOver'
import { MessageModal } from '@/components/radar/MessageModal'
import type { DashboardResponse, UrgentRequestItem } from '@/types/liveStatus'
import { getStatusColor, getStatusLabel } from '@/utils/statusHelpers'
import { getEventLabel, formatRelativeTime } from '@/utils/eventLabels'
import { useLiveStatusWebSocket, type WSStatusUpdate } from '@/hooks/useLiveStatusWebSocket'
import type { RadarBubble } from '@/services/api/teacher'
import { todayLocalISO } from '@/lib/date-utils'
import { useDirectorEnabled } from '@/hooks/useDirectorEnabled'

type TabType = 'all' | 'urgent' | 'open' | 'partial' | 'filled' | 'storno' | 'manual_assign'
type TimeRange = 'today' | 'week' | 'all'
type SortBy = 'urgency' | 'event' | 'fill'

const ACTIVITY_WINDOWS: Record<string, number> = {
  '5min': 5 * 60 * 1000,
  '15min': 15 * 60 * 1000,
  '1h': 60 * 60 * 1000,
}

function formatHoursUntil(hours: number | null): string {
  if (hours == null) return '—'
  if (hours < 1) return `in ${Math.round(hours * 60)} Min.`
  if (hours < 24) return `in ${Math.round(hours)} Std.`
  const days = Math.floor(hours / 24)
  return `in ${days} Tag${days !== 1 ? 'en' : ''}`
}

function buildSyntheticBubble(item: UrgentRequestItem, directorOn: boolean): RadarBubble {
  const hours = item.hours_until_lesson ?? 9999
  return {
    id: item.request_id,
    request_id: item.request_id,
    lesson_request_id: item.request_id,
    distance_from_center: 0,
    bubble_radius: 0,
    bubble_color: '',
    status: item.status,
    hours_until_lesson: hours,
    is_in_panic_zone: item.panic_zone_activated,
    is_in_middle_zone: !item.panic_zone_activated && hours <= 24 && hours > 4,
    is_in_outer_zone: hours > 24,
    angle_degrees: 0,
    cluster_id: null,
    cluster_index: 0,
    lesson_date: item.lesson_date,
    time_slot_name: item.time_slot_name,
    subject_name: item.subject_name,
    klass_name: item.klass_name,
    room_name: null,
    lesson_count: item.lesson_count,
    requester_teacher_id: item.requester_teacher_id ?? '',
    requester_short_code: item.requester_short_code ?? '',
    requester_name: item.requester_name,
    requester_faculty: item.faculty_name,
    assigned_teacher_id: null,
    assigned_teacher_short_code: item.assigned_teacher_short_code ?? null,
    assigned_teacher_name: item.assigned_teacher_name ?? null,
    teachers_contacted: item.teachers_contacted,
    teachers_accepted: item.teachers_accepted,
    teachers_declined: item.teachers_declined,
    teachers_pending: item.teachers_pending,
    cancellation_pending: item.cancellation_pending,
    needs_admin_assignment: item.needs_admin_assignment,
    bubble_type: 'outgoing',
    gate_pending: directorOn && item.status === 'director_pending',
  }
}

function WsBadge({ status }: { status: string }) {
  if (status === 'connected') {
    return (
      <span className="flex items-center gap-1.5 text-xs font-medium text-green-600 bg-green-50 border border-green-500 px-2 py-1 rounded-full">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
        WebSocket
      </span>
    )
  }
  if (status === 'reconnecting' || status === 'connecting') {
    return (
      <span className="text-xs text-amber-600 bg-amber-50 border border-amber-300 px-2 py-1 rounded-full">
        Reconnecting…
      </span>
    )
  }
  return (
    <span className="text-xs text-gray-500 bg-gray-100 border border-gray-300 px-2 py-1 rounded-full">
      Polling-Modus
    </span>
  )
}

function RequestCard({ item, selected, pulsing, onClick, directorOn }: {
  item: UrgentRequestItem
  selected: boolean
  pulsing: boolean
  onClick: () => void
  directorOn: boolean
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-lg border p-3 transition-all ${
        selected ? 'border-blue-500 ring-2 ring-blue-200'
        : pulsing ? 'border-blue-300 ring-2 ring-blue-100'
        : 'border-gray-200 hover:border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="font-medium text-sm truncate">
            {item.subject_name || '—'} {item.klass_name || ''}
          </p>
          <p className="text-xs text-gray-500 truncate">{item.requester_name}</p>
        </div>
        <span className={`px-2 py-0.5 text-xs rounded-full whitespace-nowrap ${getStatusColor(item.status)}`}>
          {(!directorOn && (item.status === 'director_pending' || item.status === 'director_returned'))
            ? getStatusLabel('pending')
            : getStatusLabel(item.status)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {item.requires_attention && (
          <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-700 font-medium">
            ⚠ {formatHoursUntil(item.hours_until_lesson)}
          </span>
        )}
        {!item.requires_attention && item.hours_until_lesson != null && (
          <span className="text-xs text-gray-400">{formatHoursUntil(item.hours_until_lesson)}</span>
        )}
        <span className="text-xs text-blue-600">{Math.round(item.fill_probability * 100)}%</span>
        {item.cancellation_pending && (
          <span className="px-1.5 py-0.5 text-xs rounded bg-orange-100 text-orange-700">Storno offen</span>
        )}
        {item.needs_admin_assignment && (
          <span className="px-1.5 py-0.5 text-xs rounded bg-purple-100 text-purple-700">manuelle Zuweisung nötig</span>
        )}
      </div>
      {item.latest_event && (
        <div className="flex items-center gap-1.5 mt-2 pt-2 border-t border-gray-100">
          <span className="w-1.5 h-1.5 rounded-full bg-blue-400" />
          <span className="text-xs text-gray-500 truncate">
            {item.latest_event.description || getEventLabel(item.latest_event.event_type)} · {formatRelativeTime(item.latest_event.timestamp)}
          </span>
        </div>
      )}
    </button>
  )
}

function KpiItem({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="text-center">
      <div key={value} className={`text-xl font-bold ${color} transition-all duration-300`}>
        {value}
      </div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  )
}

function EmptyStatePanel() {
  return (
    <div className="h-full flex items-center justify-center text-center p-8">
      <div>
        <div className="text-4xl mb-3 opacity-20">📋</div>
        <p className="text-gray-500 text-sm">
          Wählen Sie links eine Anfrage aus,<br />um Details und Aktionen zu sehen.
        </p>
      </div>
    </div>
  )
}

export function LiveStatusPage() {
  const navigate = useNavigate()
  const directorOn = useDirectorEnabled()
  const [searchParams, setSearchParams] = useSearchParams()

  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [timeRange, setTimeRange] = useState<TimeRange>('today')
  const [searchQuery, setSearchQuery] = useState('')
  const [searchDebounced, setSearchDebounced] = useState('')
  const [sortBy, setSortBy] = useState<SortBy>('urgency')
  const [facultyFilter, setFacultyFilter] = useState('')
  const [teacherFilter, setTeacherFilter] = useState('')
  const [activityFilter, setActivityFilter] = useState('')
  const [eventTypeFilter, setEventTypeFilter] = useState('')
  const [showMessageModal, setShowMessageModal] = useState(false)
  const [isDesktop, setIsDesktop] = useState(typeof window !== 'undefined' ? window.innerWidth >= 1024 : true)
  const [pulsingRequestIds, setPulsingRequestIds] = useState<Set<string>>(new Set())

  const fetchDashboardRef = useRef<((isRefresh?: boolean) => Promise<void>) | null>(null)
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= 1024)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(searchQuery), 300)
    return () => clearTimeout(t)
  }, [searchQuery])

  const fetchDashboard = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true)
    try {
      const params = new URLSearchParams()
      if (timeRange === 'today') {
        params.set('date_from', todayLocalISO())
      } else if (timeRange === 'week') {
        const weekAgo = new Date()
        weekAgo.setDate(weekAgo.getDate() - 7)
        params.set('date_from', todayLocalISO(weekAgo))
      }
      const qs = params.toString()
      const res = await handleApiRequest<DashboardResponse>(
        apiClient.get(`/api/v1/live-status/dashboard${qs ? `?${qs}` : ''}`)
      )
      setDashboard(res)
      setError(null)
    } catch {
      setError('Live-Status konnte nicht geladen werden')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [timeRange])

  fetchDashboardRef.current = fetchDashboard

  useEffect(() => { fetchDashboard() }, [fetchDashboard])

  const wsStatus = useLiveStatusWebSocket(useCallback((update: WSStatusUpdate) => {
    const requestId = update.request_id
    const eventType = update.event.event_type
    const timestamp = update.event.timestamp

    setPulsingRequestIds(prev => {
      const next = new Set(prev)
      next.add(requestId)
      return next
    })
    setTimeout(() => {
      setPulsingRequestIds(prev => {
        const next = new Set(prev)
        next.delete(requestId)
        return next
      })
    }, 1500)

    setDashboard(prev => {
      if (!prev) return prev
      const updated = prev.urgent_requests.map(r =>
        r.request_id === requestId
          ? {
              ...r,
              latest_event: { event_type: eventType, description: '', timestamp },
            }
          : r
      )
      return { ...prev, urgent_requests: updated }
    })

    if (refetchTimerRef.current) clearTimeout(refetchTimerRef.current)
    refetchTimerRef.current = setTimeout(() => {
      fetchDashboardRef.current?.(true)
    }, 2000)
  }, []))

  useEffect(() => {
    if (wsStatus === 'connected') return
    const interval = setInterval(() => fetchDashboardRef.current?.(true), 15000)
    return () => clearInterval(interval)
  }, [wsStatus])

  useEffect(() => {
    if (!selectedRequestId || !dashboard) return
    const stillExists = dashboard.urgent_requests.some(r => r.request_id === selectedRequestId)
    if (!stillExists) {
      setSelectedRequestId(null)
      setSearchParams({}, { replace: true })
    }
  }, [selectedRequestId, dashboard, setSearchParams])

  useEffect(() => {
    const detailParam = searchParams.get('detail')
    if (detailParam && detailParam !== selectedRequestId) {
      setSelectedRequestId(detailParam)
    } else if (!detailParam && selectedRequestId) {
      setSelectedRequestId(null)
    }
  }, [searchParams])

  const handleSelectRequest = useCallback((requestId: string) => {
    setSelectedRequestId(requestId)
    setSearchParams({ detail: requestId }, { replace: true })
  }, [setSearchParams])

  const handleCloseSlideOver = useCallback(() => {
    setSelectedRequestId(null)
    setSearchParams({}, { replace: true })
  }, [setSearchParams])

  const allRequests = dashboard?.urgent_requests ?? []

  const faculties = useMemo(() => {
    const set = new Set<string>()
    allRequests.forEach(r => { if (r.faculty_name) set.add(r.faculty_name) })
    return Array.from(set).sort()
  }, [allRequests])

  const teachers = useMemo(() => {
    const map = new Map<string, string>()
    allRequests.forEach(r => {
      if (r.requester_teacher_id) map.set(r.requester_teacher_id, r.requester_name)
    })
    return Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name))
  }, [allRequests])

  const eventTypes = useMemo(() => {
    const set = new Set<string>()
    allRequests.forEach(r => { if (r.latest_event) set.add(r.latest_event.event_type) })
    return Array.from(set).sort()
  }, [allRequests])

  const filteredRequests = useMemo(() => {
    let result = allRequests

    switch (activeTab) {
      case 'urgent': result = result.filter(r => r.requires_attention); break
      case 'open': result = result.filter(r => r.status === 'pending'); break
      case 'partial': result = result.filter(r => ['partially_filled', 'partially_accepted'].includes(r.status)); break
      case 'filled': result = result.filter(r => ['filled', 'accepted', 'booked'].includes(r.status)); break
      case 'storno': result = result.filter(r => r.cancellation_pending); break
      case 'manual_assign': result = result.filter(r => r.needs_admin_assignment); break
    }

    if (facultyFilter) result = result.filter(r => r.faculty_name === facultyFilter)
    if (teacherFilter) result = result.filter(r => r.requester_teacher_id === teacherFilter)
    if (activityFilter && ACTIVITY_WINDOWS[activityFilter]) {
      const cutoff = Date.now() - ACTIVITY_WINDOWS[activityFilter]
      result = result.filter(r => r.latest_event && new Date(r.latest_event.timestamp).getTime() > cutoff)
    }
    if (eventTypeFilter) result = result.filter(r => r.latest_event?.event_type === eventTypeFilter)

    if (searchDebounced) {
      const q = searchDebounced.toLowerCase()
      result = result.filter(r =>
        r.requester_name?.toLowerCase().includes(q) ||
        r.subject_name?.toLowerCase().includes(q) ||
        r.klass_name?.toLowerCase().includes(q)
      )
    }

    const sorted = [...result]
    if (sortBy === 'urgency') {
      sorted.sort((a, b) => {
        const ah = a.hours_until_lesson ?? 999999
        const bh = b.hours_until_lesson ?? 999999
        return ah !== bh ? ah - bh : a.request_id.localeCompare(b.request_id)
      })
    } else if (sortBy === 'event') {
      sorted.sort((a, b) => {
        const at = a.latest_event?.timestamp ?? ''
        const bt = b.latest_event?.timestamp ?? ''
        return at !== bt ? bt.localeCompare(at) : a.request_id.localeCompare(b.request_id)
      })
    } else if (sortBy === 'fill') {
      sorted.sort((a, b) => {
        return a.fill_probability !== b.fill_probability
          ? a.fill_probability - b.fill_probability
          : a.request_id.localeCompare(b.request_id)
      })
    }

    return sorted
  }, [allRequests, activeTab, facultyFilter, teacherFilter, activityFilter, eventTypeFilter, searchDebounced, sortBy])

  const selectedItem = useMemo(() => {
    if (!selectedRequestId || !dashboard) return null
    return dashboard.urgent_requests.find(r => r.request_id === selectedRequestId) ?? null
  }, [selectedRequestId, dashboard])

  const selectedBubble = useMemo(() => {
    return selectedItem ? buildSyntheticBubble(selectedItem, directorOn) : null
  }, [selectedItem, directorOn])

  const tabs: { key: TabType; label: string; count: number }[] = [
    { key: 'all', label: 'Alle', count: allRequests.length },
    { key: 'urgent', label: 'Dringend', count: allRequests.filter(r => r.requires_attention).length },
    { key: 'open', label: 'Offen', count: allRequests.filter(r => r.status === 'pending').length },
    { key: 'partial', label: 'Teilweise', count: allRequests.filter(r => ['partially_filled', 'partially_accepted'].includes(r.status)).length },
    { key: 'filled', label: 'Besetzt', count: allRequests.filter(r => ['filled', 'accepted', 'booked'].includes(r.status)).length },
    { key: 'storno', label: 'Storno', count: allRequests.filter(r => r.cancellation_pending).length },
    { key: 'manual_assign', label: 'Manuell', count: allRequests.filter(r => r.needs_admin_assignment).length },
  ]

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-pulse text-gray-500">Laden...</div>
      </div>
    )
  }

  const slideOverProps = selectedRequestId && selectedBubble ? (
    <AdminSlideOver
      requestId={selectedRequestId}
      bubble={selectedBubble}
      onClose={handleCloseSlideOver}
      onRefresh={() => fetchDashboard(true)}
      onOpenMessageModal={() => setShowMessageModal(true)}
      embedded={isDesktop}
      switchView={{
        label: 'Radar',
        onClick: () => navigate(`/admin/substitutions?detail=${selectedRequestId}`),
      }}
    />
  ) : null

  return (
    <div className="h-full flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b bg-white flex-shrink-0">
        <div className="flex items-center gap-4 min-w-0">
          <h1 className="text-lg font-semibold whitespace-nowrap">Live Operations</h1>
          <WsBadge status={wsStatus} />
          {dashboard && (
            <div className="hidden sm:flex gap-4">
              <KpiItem label="Aktiv" value={dashboard.summary.total_active_requests} color="text-blue-600" />
              <KpiItem label="Dringend" value={dashboard.urgent_requests.filter(r => r.requires_attention).length} color="text-red-600" />
              <KpiItem label="Besetzt" value={dashboard.summary.total_lessons_filled} color="text-green-600" />
              <KpiItem label="Offen" value={dashboard.summary.total_lessons_pending} color="text-orange-600" />
            </div>
          )}
        </div>
        <button
          onClick={() => navigate('/admin/substitutions')}
          className="text-sm text-blue-600 hover:text-blue-700 whitespace-nowrap ml-2"
        >
          Radar öffnen →
        </button>
      </div>

      {error && (
        <div className="mb-2 p-3 bg-red-100 text-red-700 rounded flex items-center justify-between flex-shrink-0">
          <span>{error}</span>
          <button onClick={() => fetchDashboard(true)} className="text-sm underline hover:no-underline">
            Erneut versuchen
          </button>
        </div>
      )}

      <div className="flex-1 flex overflow-hidden">
        <div className="flex-1 flex flex-col overflow-hidden">
          <div className="px-4 py-2 border-b bg-gray-50 flex-shrink-0 space-y-2">
            <div className="flex gap-1 flex-wrap">
              {tabs.map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-2.5 py-1 text-xs rounded-full transition ${
                    activeTab === tab.key
                      ? 'bg-blue-600 text-white'
                      : 'bg-white text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label} ({tab.count})
                </button>
              ))}
            </div>
            <div className="flex gap-2 flex-wrap items-center">
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Suche..."
                className="px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 w-40"
              />
              <select value={timeRange} onChange={e => setTimeRange(e.target.value as TimeRange)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                <option value="today">Heute</option>
                <option value="week">Diese Woche</option>
                <option value="all">Alle</option>
              </select>
              <select value={sortBy} onChange={e => setSortBy(e.target.value as SortBy)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                <option value="urgency">Dringlichkeit</option>
                <option value="event">Letztes Event</option>
                <option value="fill">Fill-Rate</option>
              </select>
              {faculties.length > 0 && (
                <select value={facultyFilter} onChange={e => setFacultyFilter(e.target.value)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                  <option value="">Alle Fakultäten</option>
                  {faculties.map(f => <option key={f} value={f}>{f}</option>)}
                </select>
              )}
              {teachers.length > 0 && (
                <select value={teacherFilter} onChange={e => setTeacherFilter(e.target.value)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                  <option value="">Alle Lehrer</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              )}
              <select value={activityFilter} onChange={e => setActivityFilter(e.target.value)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                <option value="">Alle Aktivität</option>
                <option value="5min">Letzte 5 Min.</option>
                <option value="15min">Letzte 15 Min.</option>
                <option value="1h">Letzte Stunde</option>
              </select>
              {eventTypes.length > 0 && (
                <select value={eventTypeFilter} onChange={e => setEventTypeFilter(e.target.value)} className="px-2 py-1 text-xs border border-gray-300 rounded">
                  <option value="">Alle Events</option>
                  {eventTypes.map(et => <option key={et} value={et}>{getEventLabel(et)}</option>)}
                </select>
              )}
              {refreshing && <span className="text-xs text-gray-400 animate-pulse">Aktualisiere...</span>}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
            {filteredRequests.length === 0 ? (
              <div className="text-center text-gray-500 py-8 text-sm">Keine Anfragen</div>
            ) : (
              filteredRequests.map(item => (
                <RequestCard
                  key={item.request_id}
                  item={item}
                  selected={item.request_id === selectedRequestId}
                  pulsing={pulsingRequestIds.has(item.request_id)}
                  onClick={() => handleSelectRequest(item.request_id)}
                  directorOn={directorOn}
                />
              ))
            )}
          </div>
        </div>

        {isDesktop && (
          <div className="w-[560px] flex-shrink-0 border-l border-gray-200">
            {slideOverProps ?? <EmptyStatePanel />}
          </div>
        )}
      </div>

      {!isDesktop && slideOverProps}

      {showMessageModal && selectedBubble && (
        <MessageModal
          bubble={selectedBubble}
          onClose={() => setShowMessageModal(false)}
          onSuccess={() => setShowMessageModal(false)}
        />
      )}
    </div>
  )
}
